import { useEffect, useState } from "react";
import { IconBrandTelegram, IconExternalLink } from "@tabler/icons-react";
import { Alert, Badge, Button, Group, Paper, Stack, Text } from "@/components/ui";
import { formatFechaHora } from "@/shared/format/fecha";
import { useDesvincularTelegram, useGenerarEnlaceTelegram, useMisCanales } from "../hooks/use-perfil-notificaciones";
import type { MisCanales } from "../services/perfil-notificaciones.api";

/**
 * Vinculación con el bot: el portal genera un enlace de un solo uso (t.me/&lt;bot&gt;?start=…); al pulsar
 * "Iniciar" en Telegram, el sondeo del servidor detecta el mensaje y guarda el chat. Mientras tanto
 * esta tarjeta vuelve a consultar cada pocos segundos para mostrar "Vinculado".
 */
export function CardTelegram({ telegram }: { telegram: MisCanales["telegram"] }) {
  const generar = useGenerarEnlaceTelegram();
  const desvincular = useDesvincularTelegram();
  const [enlace, setEnlace] = useState<{ enlace: string; expiraAt: string } | null>(null);
  const esperando = enlace !== null && !telegram.vinculado && new Date(enlace.expiraAt) > new Date();
  useMisCanales({ sondear: esperando });

  useEffect(() => {
    if (telegram.vinculado) setEnlace(null);
  }, [telegram.vinculado]);

  return (
    <Paper withBorder padding="lg">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <IconBrandTelegram size={18} className="text-multiva-600" />
            <Text fw={600}>Telegram</Text>
          </Group>
          {telegram.vinculado ? <Badge color="exito">Vinculado</Badge> : null}
        </Group>

        {!telegram.disponible ? (
          <Text size="sm" c="dimmed">El bot de Telegram del portal aún no está configurado.</Text>
        ) : telegram.vinculado ? (
          <>
            <Text size="sm">
              Recibes notificaciones en {telegram.nombreEnTelegram ?? "tu cuenta de Telegram"}
              {telegram.vinculadoAt ? ` · desde ${formatFechaHora(telegram.vinculadoAt)}` : ""}.
            </Text>
            <Group gap="xs">
              <Button variant="default" size="sm" loading={desvincular.isPending} onClick={() => desvincular.mutate()}>
                Desvincular
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              Abre el enlace, pulsa <strong>Iniciar</strong> en el chat con @{telegram.nombreBot} y esta página mostrará tu cuenta vinculada.
            </Text>
            {esperando ? (
              <Alert variant="info" title="Esperando a que pulses Iniciar en Telegram…">
                <a href={enlace!.enlace} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-multiva-700 underline dark:text-multiva-300">
                  Abrir Telegram <IconExternalLink size={14} />
                </a>
                <Text size="xs" c="dimmed" className="mt-1">El enlace vence a las {formatFechaHora(enlace!.expiraAt)}.</Text>
              </Alert>
            ) : null}
            <Group gap="xs">
              <Button
                size="sm"
                leftSection={<IconBrandTelegram size={14} />}
                loading={generar.isPending}
                onClick={() =>
                  generar.mutate(undefined, {
                    onSuccess: (r) => {
                      setEnlace(r);
                      window.open(r.enlace, "_blank", "noopener,noreferrer");
                    },
                  })
                }
              >
                {esperando ? "Generar otro enlace" : "Vincular Telegram"}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Paper>
  );
}
